import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Smartphone, Heart, Calendar, Target, BookOpen, Users, 
  Bell, Shield, CheckCircle, Star, ArrowRight, Sparkles,
  TrendingUp, Brain, Clock, MessageCircle, Download,
  Activity, Flame, UserCheck, Eye, HeartHandshake
} from 'lucide-react';

const APP_URL = 'https://centros-staging.preview.emergentagent.com/';

export function AppLandingPage() {
  const features = {
    enRecuperacion: [
      {
        icon: Calendar,
        title: 'Seguimiento Diario',
        description: 'Registra tus hábitos, emociones y días limpio de forma simple y privada.'
      },
      {
        icon: Target,
        title: 'Sobriedad con Sentido',
        description: 'Define tu propósito y objetivos de vida para mantener tu motivación.'
      },
      {
        icon: TrendingUp,
        title: 'Estadísticas de Progreso',
        description: 'Visualiza tu avance día a día y celebra cada logro.'
      }
    ],
    quieroDejarlo: [
      {
        icon: Flame,
        title: 'Reto de 21 Días',
        description: 'Programa guiado paso a paso para comenzar tu camino a la recuperación.'
      },
      {
        icon: BookOpen,
        title: 'Contenido Educativo',
        description: 'Entiende la adicción, el craving y cómo funciona tu cerebro.'
      },
      {
        icon: Brain,
        title: 'Técnicas de Afrontamiento',
        description: 'Herramientas prácticas para manejar los momentos difíciles.'
      }
    ],
    profesionales: [
      {
        icon: UserCheck,
        title: 'Gestión de Pacientes',
        description: 'Vincula y monitorea a tus pacientes de forma segura.'
      },
      {
        icon: Bell,
        title: 'Alertas Inteligentes',
        description: 'Recibe notificaciones de recaídas, inactividad y cambios emocionales.'
      },
      {
        icon: Activity,
        title: 'Dashboard Clínico',
        description: 'Visualiza el progreso de todos tus pacientes en un solo lugar.'
      }
    ],
    familiares: [
      {
        icon: HeartHandshake,
        title: 'Educación Familiar',
        description: 'Aprende a apoyar sin habilitar la adicción de tu ser querido.'
      },
      {
        icon: Eye,
        title: 'Seguimiento Autorizado',
        description: 'Ve el progreso de tu familiar con su permiso explícito.'
      },
      {
        icon: MessageCircle,
        title: 'Recursos de Apoyo',
        description: 'Guías y consejos para cuidar tu propia salud emocional.'
      }
    ]
  };

  const benefits = [
    { icon: Shield, text: '100% Privado y Seguro' },
    { icon: Star, text: 'Completamente Gratuita' },
    { icon: Clock, text: 'Disponible 24/7' },
    { icon: Smartphone, text: 'Funciona sin internet' }
  ];

  // Schema for App
  const appSchema = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Sin Adicciones App",
    "operatingSystem": "iOS, Android, Web",
    "applicationCategory": "HealthApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CLP"
    },
    "description": "Aplicación gratuita para la recuperación de adicciones. Seguimiento diario, reto de 21 días, contenido educativo y herramientas para profesionales y familiares.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "127"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>App Sin Adicciones | Herramienta Gratuita para la Recuperación | Beta</title>
        <meta name="description" content="Descarga gratis la app de Sin Adicciones. Seguimiento diario, reto de 21 días, contenido educativo. Para personas en recuperación, profesionales y familiares." />
        <link rel="canonical" href="https://sinadicciones.org/app" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/app" />
        <meta property="og:title" content="App Sin Adicciones - Tu Compañero en la Recuperación" />
        <meta property="og:description" content="App gratuita para la recuperación de adicciones. Seguimiento diario, reto de 21 días y contenido educativo. Ahora en Beta." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="App Sin Adicciones - Tu Compañero en la Recuperación" />
        <meta name="twitter:description" content="App gratuita para la recuperación. Beta abierta." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
      

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-b border-slate-800 py-20 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-10 left-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-20 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {/* Beta Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 font-semibold">BETA GRATUITA</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 mb-6 leading-tight">
                  Tu Compañero<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                    En La Recuperación
                  </span>
                </h1>

                <p className="text-xl text-slate-300 mb-8">
                  La primera app chilena diseñada para acompañarte en cada paso de tu camino hacia una vida libre de adicciones.
                  <strong className="text-emerald-400"> 100% gratuita.</strong>
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                      <Smartphone className="w-5 h-5 mr-2" />
                      Probar App Gratis
                    </a>
                  </Button>
                  <Button 
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-xl px-8 py-6 text-lg"
                  >
                    <a href="#caracteristicas">
                      Ver Características
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                </div>

                {/* Mini Benefits */}
                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                  {benefits.map((b, idx) => {
                    const Icon = b.icon;
                    return (
                      <span key={idx} className="flex items-center gap-1">
                        <Icon className="w-4 h-4 text-emerald-400" />
                        {b.text}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Phone Mockup */}
              <div className="hidden lg:flex justify-center">
                <div className="relative">
                  {/* Glow Effect */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-[3rem] blur-2xl"></div>
                  
                  {/* Phone Frame */}
                  <div className="relative bg-slate-900 rounded-[2.5rem] p-3 border-4 border-slate-700 shadow-2xl">
                    <div className="bg-slate-950 rounded-[2rem] overflow-hidden w-[280px] h-[560px]">
                      {/* Status Bar */}
                      <div className="h-8 bg-slate-900 flex items-center justify-center">
                        <div className="w-20 h-5 bg-slate-800 rounded-full"></div>
                      </div>
                      
                      {/* App Content Preview */}
                      <div className="p-4 space-y-4">
                        <div className="text-center mb-6">
                          <div className="w-12 h-12 rounded-xl bg-emerald-500 mx-auto mb-2 flex items-center justify-center">
                            <Heart className="w-6 h-6 text-white" />
                          </div>
                          <p className="text-slate-50 font-semibold">Sin Adicciones</p>
                          <p className="text-xs text-slate-500">Tu primer paso</p>
                        </div>
                        
                        {/* Feature Cards Preview */}
                        <div className="space-y-3">
                          <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-50">En Recuperación</p>
                                <p className="text-xs text-slate-500">Seguimiento diario</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                                <Flame className="w-5 h-5 text-cyan-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-50">Quiero Dejarlo</p>
                                <p className="text-xs text-slate-500">Reto de 21 días</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-amber-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-50">Para Familiares</p>
                                <p className="text-xs text-slate-500">Apoyo informado</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-emerald-400">100%</p>
                <p className="text-slate-400 text-sm mt-1">Gratuita</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-emerald-400">4</p>
                <p className="text-slate-400 text-sm mt-1">Perfiles de usuario</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-emerald-400">21</p>
                <p className="text-slate-400 text-sm mt-1">Días de reto guiado</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-emerald-400">24/7</p>
                <p className="text-slate-400 text-sm mt-1">Disponible siempre</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="caracteristicas" className="py-20 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                Una App Para <span className="text-emerald-400">Cada Etapa</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Diseñada pensando en todos los que luchan contra las adicciones: 
                personas en recuperación, quienes quieren empezar, profesionales y familiares.
              </p>
            </div>

            {/* En Recuperación */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-50">En Recuperación</h3>
                  <p className="text-slate-400">Para quienes ya están en su camino</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {features.enRecuperacion.map((f, idx) => {
                  const Icon = f.icon;
                  return (
                    <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-emerald-400" />
                        </div>
                        <h4 className="font-semibold text-slate-50 mb-2">{f.title}</h4>
                        <p className="text-sm text-slate-400">{f.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Quiero Dejarlo */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Flame className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-50">Quiero Dejarlo</h3>
                  <p className="text-slate-400">Para quienes quieren dar el primer paso</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {features.quieroDejarlo.map((f, idx) => {
                  const Icon = f.icon;
                  return (
                    <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-cyan-500/50 transition-all">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-cyan-400" />
                        </div>
                        <h4 className="font-semibold text-slate-50 mb-2">{f.title}</h4>
                        <p className="text-sm text-slate-400">{f.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Para Profesionales */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-50">Para Profesionales</h3>
                  <p className="text-slate-400">Herramientas clínicas avanzadas</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {features.profesionales.map((f, idx) => {
                  const Icon = f.icon;
                  return (
                    <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-purple-500/50 transition-all">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-purple-400" />
                        </div>
                        <h4 className="font-semibold text-slate-50 mb-2">{f.title}</h4>
                        <p className="text-sm text-slate-400">{f.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Para Familiares */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-50">Para Familiares</h3>
                  <p className="text-slate-400">Aprende a acompañar sin habilitar</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {features.familiares.map((f, idx) => {
                  const Icon = f.icon;
                  return (
                    <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-amber-500/50 transition-all">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-amber-400" />
                        </div>
                        <h4 className="font-semibold text-slate-50 mb-2">{f.title}</h4>
                        <p className="text-sm text-slate-400">{f.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Reto 21 Días Highlight */}
        <section className="py-20 border-b border-slate-800 bg-gradient-to-b from-slate-900/50 to-slate-950">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 rounded-full mb-6">
                  <Flame className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 font-semibold">Programa Guiado</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                  El Reto de <span className="text-cyan-400">21 Días</span>
                </h2>
                <p className="text-lg text-slate-300 mb-6">
                  Un programa paso a paso diseñado para quienes quieren dar el primer paso hacia la recuperación. 
                  21 días de contenido diario, ejercicios prácticos y motivación constante.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>Contenido nuevo cada día durante 21 días</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>Técnicas prácticas para manejar el craving</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>Ejercicios de reflexión y autoconocimiento</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>Sin costo, sin publicidad, sin juicios</span>
                  </li>
                </ul>
                <Button 
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-xl px-8"
                >
                  <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                    <Flame className="w-5 h-5 mr-2" />
                    Comenzar el Reto
                  </a>
                </Button>
              </div>
              
              {/* Days Preview */}
              <div className="hidden lg:block">
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: 21 }, (_, i) => (
                    <div 
                      key={i}
                      className={`aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all
                        ${i < 7 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 
                          i < 14 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                          'bg-amber-500/20 text-amber-400 border border-amber-500/40'}`}
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-center gap-8 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-cyan-500/20 border border-cyan-500/40"></div>
                    <span className="text-slate-400">Semana 1: Preparación</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-emerald-500/20 border border-emerald-500/40"></div>
                    <span className="text-slate-400">Semana 2: Acción</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-500/20 border border-amber-500/40"></div>
                    <span className="text-slate-400">Semana 3: Consolidación</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy & Security */}
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 mb-6">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold text-slate-50 mb-4">
              Tu Privacidad es <span className="text-emerald-400">Sagrada</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Entendemos lo delicado de tu situación. Por eso, la app está diseñada con la máxima privacidad:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <Shield className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-50 mb-2">Datos Encriptados</h4>
                  <p className="text-sm text-slate-400">Tu información está protegida con encriptación de nivel bancario.</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <Eye className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-50 mb-2">Sin Rastreo</h4>
                  <p className="text-sm text-slate-400">No vendemos ni compartimos tus datos con terceros. Nunca.</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <UserCheck className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-50 mb-2">Tú Controlas</h4>
                  <p className="text-sm text-slate-400">Decides qué compartir y con quién. Puedes borrar tus datos en cualquier momento.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-semibold">GRATIS EN BETA</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
              Da el Primer Paso <span className="text-emerald-400">Hoy</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              No importa en qué etapa estés. La app está diseñada para acompañarte en cada momento de tu camino hacia una vida libre de adicciones.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl px-10 py-6 text-lg font-semibold shadow-lg"
              >
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  <Smartphone className="w-5 h-5 mr-2" />
                  Probar App Gratis
                </a>
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-6">
              Sin tarjeta de crédito. Sin publicidad. Sin juicios.
            </p>
          </div>
        </section>

        {/* Link to Other Resources */}
        <section className="py-12 border-t border-slate-800 bg-slate-900/30">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h3 className="text-xl font-bold text-slate-50 text-center mb-8">
              Más Recursos de Sin Adicciones
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/centros-de-rehabilitacion-drogas-chile">
                <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                  <CardContent className="p-5 text-center">
                    <HeartHandshake className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-medium text-slate-50">Centros</p>
                    <p className="text-xs text-slate-500">Encuentra tratamiento</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/profesionales">
                <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                  <CardContent className="p-5 text-center">
                    <UserCheck className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-medium text-slate-50">Profesionales</p>
                    <p className="text-xs text-slate-500">Especialistas verificados</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/herramientas">
                <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                  <CardContent className="p-5 text-center">
                    <Brain className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-medium text-slate-50">Tests</p>
                    <p className="text-xs text-slate-500">Evalúa tu situación</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/asesoria">
                <Card className="bg-slate-900 border-slate-800 hover:border-amber-500/50 transition-all h-full">
                  <CardContent className="p-5 text-center">
                    <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <p className="font-medium text-slate-50">Asesoría</p>
                    <p className="text-xs text-slate-500">Orientación experta</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default AppLandingPage;
